import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Mic,
  MicOff,
  Users,
  MessageCircle,
  Image,
  Settings,
  Radio,
  Send,
  Play,
  Download,
  UserCheck,
  UserX,
  Volume2,
  VolumeX,
  Phone,
  PhoneOff,
  Wifi,
  WifiOff,
  Signal,
  SignalHigh,
  SignalLow,
  MessageSquare,
  Clock,
  Headphones,
  ArrowRight,
  X,
  Shield,
  Activity,
  Zap,
} from "lucide-react";
import { ThemeContext } from "./theme";
import { io } from "socket.io-client";
import * as pttApi from "./pptAPI"; // Add this import at the top
import WebRTCManager from "./webRTCManager";
import { getEnglishInitials } from "../components/UserAvatar";
const isMobile = window.innerWidth <= 768;
const isTablet = window.innerWidth > 768 && window.innerWidth <= 1024;
const SOCKET_URL = process.env.REACT_APP_BASE_SERVER_URL;

const Connector = () => {
  const { theme } = useContext(ThemeContext);

  // Initial sample messages
  const [messages, setMessages] = useState([]);

  // Sample users data for demonstration
  const [connectedUsers, setConnectedUsers] = useState([]);

  // Communication state variables
  const [isConnected, setIsConnected] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [channelConnected, setChannelConnected] = useState(false);
  const [currentChannelId, setCurrentChannelId] = useState(null); // ADD THIS LINE
  const [isPushToTalk, setIsPushToTalk] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [audioPermissions, setAudioPermissions] = useState({});
  const [currentSpeaker, setCurrentSpeaker] = useState(null);
  const [volume, setVolume] = useState(80);
  const [isMuted, setIsMuted] = useState(false);
  const [networkLatency, setNetworkLatency] = useState(0);
  const [signalStrength, setSignalStrength] = useState(100);

  // Recording timer state
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordingStartTime, setRecordingStartTime] = useState(null);

  // UI state variables
  const [showMessagesModal, setShowMessagesModal] = useState(false);
  const [talkMode, setTalkMode] = useState("hold"); // "hold" or "toggle"
  const [selectedUser, setSelectedUser] = useState(null);
  const [autoManageUsers, setAutoManageUsers] = useState(true);
  // Add after existing state declarations (around line 40)
  const [subscribedChannels, setSubscribedChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null); // Channel to transmit to
  const [channelActivity, setChannelActivity] = useState({}); // Track which channels have active speakers

  // New state for modals
  const [showCreateChannelModal, setShowCreateChannelModal] = useState(false);
  const [showOnlineUsersModal, setShowOnlineUsersModal] = useState(false);
  const [allOnlineUsers, setAllOnlineUsers] = useState([]);
  const [newChannelName, setNewChannelName] = useState("");
  const [newChannelDescription, setNewChannelDescription] = useState("");
  const [newChannelType, setNewChannelType] = useState("group");
  const [isChannelPrivate, setIsChannelPrivate] = useState(false);

  const webrtcManagerRef = useRef(null);
  const [useWebRTC, setUseWebRTC] = useState(true); // Toggle WebRTC vs Socket.IO
  const [webrtcStats, setWebrtcStats] = useState(null);
  const [playingAudio, setPlayingAudio] = useState(null);

  // Initialize WebRTC manager when socket connects
  useEffect(() => {
    const socket = socketRef.current;

    if (socket && socketConnected && !webrtcManagerRef.current) {
      const infinityCode = localStorage.getItem("infinityCode");
      const employeeName = localStorage.getItem("employeeName");

      webrtcManagerRef.current = new WebRTCManager(
        socket,
        infinityCode,
        employeeName
      );

      console.log("✅ WebRTC Manager initialized");
    }

    return () => {
      if (webrtcManagerRef.current) {
        webrtcManagerRef.current.cleanup();
        webrtcManagerRef.current = null;
      }
    };
  }, [socketConnected]);
  // Monitor WebRTC stats periodically
  useEffect(() => {
    if (!useWebRTC || !webrtcManagerRef.current) return;

    const statsInterval = setInterval(async () => {
      if (
        webrtcManagerRef.current &&
        webrtcManagerRef.current.peerConnections.size > 0
      ) {
        const peerId = Array.from(
          webrtcManagerRef.current.peerConnections.keys()
        )[0];
        const stats = await webrtcManagerRef.current.getStats(peerId);

        if (stats && stats.length > 0) {
          setWebrtcStats(stats[0]);

          // Update network latency from RTT
          if (stats[0].roundTripTime) {
            setNetworkLatency(Math.round(stats[0].roundTripTime * 1000));
          }
        }
      }
    }, 2000);

    return () => clearInterval(statsInterval);
  }, [useWebRTC, isPushToTalk]);

  // Component references
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const pushToTalkButtonRef = useRef(null);
  const socketRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const recordingIntervalRef = useRef(null);
  const [allEmployees, setAllEmployees] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const loadAllEmployees = async () => {
    try {
      const result = await pttApi.getAllEmployees(); // You need to create this API endpoint
      if (result.success && result.data) {
        setAllEmployees(result.data);
      }
    } catch (error) {
      console.error("Error loading employees:", error);
    }
  };

  // Socket.IO connection setup
  useEffect(() => {
    const token = localStorage.getItem("token"); // Use "token" not "jwt"

    if (!token) {
      console.error("No JWT token found - user needs to login first");
      // Redirect to login or show error
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          type: "system",
          content: "خطأ: يجب تسجيل الدخول أولاً للوصول إلى النظام",
          timestamp: new Date(),
        },
      ]);
      return;
    }

    // Create socket connection with real JWT token
    socketRef.current = io(SOCKET_URL, {
      auth: {
        token: token,
      },
      autoConnect: false,
    });

    const socket = socketRef.current;

    // Socket event listeners
    socket.on("connect", () => {
      console.log("Socket connected:", socket.id);
      setSocketConnected(true);
    });

    socket.on("disconnect", () => {
      console.log("Socket disconnected");
      setSocketConnected(false);
      setIsConnected(false);
    });

    socket.on("connect_error", (error) => {
      console.error("Socket connection error:", error.message);
      setSocketConnected(false);

      // Handle specific authentication errors
      if (error.message.includes("Token expired")) {
        const expiredMessage = {
          id: Date.now(),
          type: "system",
          content: "انتهت صلاحية الجلسة، يرجى تسجيل الدخول مرة أخرى",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, expiredMessage]);

        // Clear localStorage and redirect to login
        localStorage.clear();
        // window.location.href = "/login"; // Uncomment if you want automatic redirect
      } else if (
        error.message.includes("Invalid token") ||
        error.message.includes("No token")
      ) {
        const authMessage = {
          id: Date.now(),
          type: "system",
          content: "خطأ في المصادقة، يرجى تسجيل الدخول مرة أخرى",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, authMessage]);
      }
    });

    socket.on("user_joined", (data) => {
      console.log("User joined:", data);
      const joinMessage = {
        id: Date.now(),
        type: "system",
        content: `انضم مستخدم جديد إلى القناة`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, joinMessage]);
    });

    socket.on("user_talking", (data) => {
      console.log("User talking:", data);
      setCurrentSpeaker(`User ${data.userId}`);
    });

    socket.on("user_stopped", (data) => {
      console.log("User stopped talking:", data);
      setCurrentSpeaker(null);
    });

    // Real-time audio streaming (for push-to-talk)
    socket.on("audio_data", (data) => {
      const { channelId, audioData, userName, userId, infinityCode } = data;

      // Don't play your own audio back
      const myInfinityCode = String(localStorage.getItem("infinityCode"));
      if (infinityCode === myInfinityCode) {
        return;
      }

      console.log(`🔊 Receiving real-time audio from ${userName}`);

      try {
        // Create audio blob with proper MIME type
        const audioBlob = new Blob([audioData], {
          type: "audio/webm;codecs=opus",
        });
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);

        audio.volume = isMuted ? 0 : volume / 100;

        audio
          .play()
          .then(() =>
            console.log(`✅ Playing real-time audio from ${userName}`)
          )
          .catch((err) => {
            console.error("❌ Real-time audio play failed:", err);
            // Try alternative playback method
            const altAudio = new Audio();
            altAudio.src = audioUrl;
            altAudio.volume = audio.volume;
            altAudio
              .play()
              .catch((e) => console.error("Alternative play also failed:", e));
          });

        audio.addEventListener("ended", () => {
          URL.revokeObjectURL(audioUrl);
        });

        audio.addEventListener("error", (e) => {
          console.error("Audio element error:", e);
          URL.revokeObjectURL(audioUrl);
        });
      } catch (error) {
        console.error("Error processing real-time audio:", error);
      }
    });

    socket.on("new_message", (message) => {
      console.log("📨 New message received:", message);

      const myInfinityCode = String(localStorage.getItem("infinityCode"));

      // Skip if this is your own message
      if (message.senderCode === myInfinityCode) {
        console.log("Skipping own message from socket");
        return;
      }

      setMessages((prevMessages) => {
        const exists = prevMessages.some((msg) => msg.id === message.id);
        if (exists) return prevMessages;

        const formattedMessage = {
          id: message.id,
          type: "text",
          sender: message.sender?.sr,
          senderCode: message.senderCode,
          content: message.textContent,
          timestamp: new Date(message.created_at),
          channelId: message.channelId,
        };

        return [...prevMessages, formattedMessage];
      });
    });

    socket.on("voice_message", (data) => {
      console.log("📩 Received voice message:", data);

      const myInfinityCode = String(localStorage.getItem("infinityCode"));

      // Skip if this is your own voice message
      if (data.senderCode === myInfinityCode) {
        console.log("Skipping own voice message");
        return;
      }

      const audioUrl = `https://nexploy-eg.com${data.audioUrl}`;

      const voiceMessage = {
        id: data.id || Date.now(),
        type: "voice",
        sender: data.senderName || "Unknown",
        senderCode: data.senderCode,
        content: "Voice message",
        timestamp: new Date(data.timestamp || data.created_at),
        duration: data.duration || "0:00",
        audioUrl: audioUrl,
      };

      setMessages((prev) => [...prev, voiceMessage]);
      /*

      // Auto-play
      setTimeout(() => {
        const audio = new Audio(audioUrl);
        audio.volume = isMuted ? 0 : volume / 100;
        audio.play().catch((err) => {
          console.error("Error auto-playing audio:", err);
        });
      }, 100);
      */
    });

    // Cleanup on unmount
    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, []);

  // Load user channels on component mount
  useEffect(() => {
    const loadUserChannels = async () => {
      try {
        const result = await pttApi.getUserChannels();
        if (result.success && result.data) {
          console.log("User channels:", result.data);
          setSubscribedChannels(result.data);

          // Auto-select first channel
          if (result.data.length > 0 && !selectedChannel) {
            setSelectedChannel(result.data[0].id);
          }
        }
      } catch (error) {
        console.error("Error loading channels:", error);
      }
    };

    const token = localStorage.getItem("token");
    if (token) {
      loadUserChannels();
    }
  }, []); // Load immediately on mount

  // Subscribe to all user channels when connected
  useEffect(() => {
    const socket = socketRef.current;

    if (socket && socketConnected && subscribedChannels.length > 0) {
      const channelIds = subscribedChannels.map((ch) => ch.id);
      console.log("📻 Subscribing to channels:", channelIds);
      socket.emit("subscribe_channels", channelIds);
    }
  }, [socketConnected, subscribedChannels]);

  // Auto-scroll messages to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Initialize audio permissions
  useEffect(() => {
    initializeAudio();
  }, []);

  // Keyboard shortcuts for push-to-talk
  useEffect(() => {
    if (talkMode !== "hold") return;

    const handleKeyDown = (e) => {
      if (isInputActive() || e.repeat || !isConnected) return;

      if (e.code === "Space" && !isPushToTalk) {
        e.preventDefault();
        startPushToTalk();
      }
    };

    const handleKeyUp = (e) => {
      if (isInputActive()) return;

      if (e.code === "Space" && isPushToTalk) {
        e.preventDefault();
        stopPushToTalk();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
    };
  }, [isPushToTalk, isConnected, talkMode]);

  // Recording timer management
  useEffect(() => {
    if (isRecording && recordingStartTime) {
      recordingIntervalRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - recordingStartTime) / 1000);
        setRecordingTime(elapsed);
      }, 100);
    } else {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }

    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    };
  }, [isRecording, recordingStartTime]);

  // Network monitoring
  useEffect(() => {
    const monitorNetwork = setInterval(() => {
      const socket = socketRef.current;
      if (socket && socket.connected) {
        const startTime = Date.now();
        socket.emit("ping", startTime);

        socket.once("pong", (startTime) => {
          const latency = Date.now() - startTime;
          setNetworkLatency(latency);
          updateSignalStrength(latency);
        });
      }
    }, 3000);

    return () => clearInterval(monitorNetwork);
  }, []);

  // Auto-manage users effect
  useEffect(() => {
    if (autoManageUsers) {
      const interval = setInterval(() => {
        autoUpdateUserStatuses();
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [autoManageUsers, connectedUsers]);

  // Helper functions
  const isInputActive = () => {
    const activeElement = document.activeElement;
    return (
      activeElement &&
      (activeElement.tagName === "INPUT" ||
        activeElement.tagName === "TEXTAREA" ||
        activeElement.contentEditable === "true")
    );
  };
  // Clear channel activity indicators after delay
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setChannelActivity((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((channelId) => {
          if (now - updated[channelId].timestamp > 2000) {
            delete updated[channelId];
          }
        });
        return updated;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // In Connector.jsx - Add polling for online users
  useEffect(() => {
    if (!currentChannelId || !isConnected) return;

    // Initial load
    loadOnlineUsers(currentChannelId);

    // Poll every 15 seconds
    const pollInterval = setInterval(() => {
      loadOnlineUsers(currentChannelId);
    }, 15000);

    return () => clearInterval(pollInterval);
  }, [currentChannelId, isConnected]);

  // Also listen to socket events for real-time updates
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    // Update user list when someone joins/leaves
    socket.on("user_joined", (data) => {
      if (data.channelId === currentChannelId) {
        loadOnlineUsers(currentChannelId);
      }
    });

    socket.on("user_left", (data) => {
      if (data.channelId === currentChannelId) {
        loadOnlineUsers(currentChannelId);
      }
    });

    socket.on("user_talking", (data) => {
      console.log("User talking event:", data);
      setCurrentSpeaker(data.userName);

      if (data.channelId === currentChannelId) {
        // Update specific user status to "talking"
        setConnectedUsers((prev) =>
          prev.map((user) =>
            user.id === data.infinityCode
              ? { ...user, status: "talking", lastActivity: new Date() }
              : user
          )
        );
      }
    });

    socket.on("user_stopped", (data) => {
      console.log("User stopped talking:", data);

      if (currentSpeaker === data.userName) {
        setCurrentSpeaker(null);
      }

      if (data.channelId === currentChannelId) {
        // Update specific user status to "online"
        setConnectedUsers((prev) =>
          prev.map((user) =>
            user.id === data.infinityCode
              ? { ...user, status: "online", lastActivity: new Date() }
              : user
          )
        );
      }

      // Clear channel activity
      setChannelActivity((prev) => {
        const updated = { ...prev };
        delete updated[data.channelId];
        return updated;
      });
    });
    return () => {
      socket.off("user_joined");
      socket.off("user_left");
      socket.off("user_talking");
      socket.off("user_stopped");
    };
  }, [currentChannelId]);

  const handleAuthError = (error) => {
    if (error.message.includes("Token expired")) {
      addSystemMessage("Session expired, please login again");
      localStorage.clear();
    } else if (
      error.message.includes("Invalid token") ||
      error.message.includes("No token")
    ) {
      addSystemMessage("Authentication error, please login again");
    }
  };

  const handleUserJoined = (data) => {
    const newUser = {
      id: data.userId || Date.now(),
      name: data.userName || `User ${data.userId}`,
      avatar: getRandomAvatar(),
      status: "online",
      permission: "pending",
      joinTime: new Date(),
      lastActivity: new Date(),
    };

    setConnectedUsers((prev) => {
      const exists = prev.find((user) => user.id === newUser.id);
      if (!exists) {
        return [...prev, newUser];
      }
      return prev.map((user) =>
        user.id === newUser.id
          ? { ...user, status: "online", lastActivity: new Date() }
          : user
      );
    });

    addSystemMessage(`${newUser.name} joined the channel`);
  };

  const handleUserLeft = (data) => {
    setConnectedUsers((prev) =>
      prev.map((user) =>
        user.id === data.userId
          ? { ...user, status: "offline", lastActivity: new Date() }
          : user
      )
    );

    addSystemMessage(`User ${data.userId} left the channel`);
  };

  const handleIncomingAudio = (data) => {
    try {
      const audioBlob = new Blob([data.audioData], { type: "audio/webm" });
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audio.volume = isMuted ? 0 : volume / 100;
      audio.play().catch((err) => console.log("Audio play failed:", err));
    } catch (error) {
      console.error("Error processing audio data:", error);
    }
  };

  const initializeAudio = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100,
        },
      });

      setAudioPermissions({ granted: true });
      audioContextRef.current = new (window.AudioContext ||
        window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      stream.getTracks().forEach((track) => track.stop());
    } catch (error) {
      console.error("Audio permission error:", error);
      setAudioPermissions({ granted: false, error: error.message });
    }
  };

  const updateSignalStrength = (latency) => {
    let strength = 100;
    if (latency > 200) strength = 30;
    else if (latency > 100) strength = 60;
    else if (latency > 50) strength = 80;
    setSignalStrength(strength);
  };

  const updateUserStatus = (userId, status) => {
    setConnectedUsers((prev) =>
      prev.map((user) =>
        user.id === userId
          ? { ...user, status, lastActivity: new Date() }
          : user
      )
    );
  };

  const autoUpdateUserStatuses = () => {
    const now = new Date();
    setConnectedUsers((prev) =>
      prev.map((user) => {
        const timeSinceActivity = now - user.lastActivity;
        const fiveMinutes = 5 * 60 * 1000;

        if (timeSinceActivity > fiveMinutes && user.status !== "offline") {
          return { ...user, status: "offline" };
        }
        return user;
      })
    );
  };

  // Uses English-only initials instead of random emojis for consistent avatar display
  const getRandomAvatar = (userName, englishName = null) => {
    // Return English initials instead of random emojis
    return getEnglishInitials(userName, englishName, "US");
  };

  const addSystemMessage = (content) => {
    const systemMessage = {
      id: Date.now(),
      type: "system",
      content,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, systemMessage]);
  };

  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDuration = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) return `${hours}h ${minutes % 60}m ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return "Just now";
  };

  const playAudio = (audioUrl, messageId) => {
    try {
      const fullUrl = audioUrl.startsWith("http")
        ? audioUrl
        : `https://nexploy-eg.com${audioUrl}`; // Replace with your backend URL

      console.log("Attempting to play audio from:", fullUrl);

      const audio = new Audio(fullUrl);
      audio.volume = isMuted ? 0 : volume / 100;

      setPlayingAudio(messageId);

      audio.addEventListener("canplay", () => {
        console.log("Audio ready to play");
      });

      audio.addEventListener("ended", () => {
        setPlayingAudio(null);
      });

      audio.addEventListener("error", (e) => {
        console.error("Audio playback error:", e);
        setPlayingAudio(null);
        addSystemMessage(`Failed to play audio. Check console for details.`);
      });

      audio.play().catch((err) => {
        console.error("Audio play failed:", err);
        setPlayingAudio(null);
        addSystemMessage(`Cannot play audio: ${err.message}`);
      });
    } catch (error) {
      console.error("Error playing audio:", error);
      setPlayingAudio(null);
      addSystemMessage(`Audio error: ${error.message}`);
    }
  };

  // Connection management

  // Replace the toggleConnection function:
  const toggleConnection = async () => {
    const socket = socketRef.current;

    // ✅ 1. Check if a channel is selected
    if (!selectedChannel) {
      console.error("No channel selected. Please select a channel first.");
      addSystemMessage("Error: No channel selected.");
      return;
    }

    if (!socket) {
      console.error("Socket not initialized");
      addSystemMessage(
        "Error: Connection not initialized. Please refresh the page."
      );
      return;
    }

    // ✅ 2. Handle channel switching while connected
    if (
      isConnected &&
      currentChannelId &&
      currentChannelId !== selectedChannel
    ) {
      console.log(
        `Switching from channel ${currentChannelId} to ${selectedChannel}...`
      );
      addSystemMessage(`Switching to channel ${selectedChannel}...`);

      // Stop any active recording first
      if (isPushToTalk || isRecording) {
        console.log("Stopping active transmission before switching...");
        stopPushToTalk();
        await new Promise((resolve) => setTimeout(resolve, 500)); // Wait for cleanup
      }

      // Leave old channel via API
      try {
        await pttApi.leaveChannel(currentChannelId);
        console.log("Successfully left old channel via API");
      } catch (e) {
        console.warn("Leave channel API warning:", e);
      }

      // Emit socket leave event for old channel
      socket.emit("leave_channel", currentChannelId);

      // Clear old channel state
      setIsConnected(false);
      setChannelConnected(false);
      setCurrentChannelId(null);
      setMessages([]); // Clear messages from old channel
      setCurrentSpeaker(null);

      // Small delay to ensure clean disconnect
      await new Promise((resolve) => setTimeout(resolve, 300));
    }

    // ✅ 3. Connect/Disconnect logic
    if (!isConnected) {
      try {
        // Ensure socket is connected first
        if (!socketConnected) {
          console.log("Connecting to socket server...");
          socket.connect();

          // Wait for socket connection with timeout
          const connected = await Promise.race([
            new Promise((resolve) => {
              const checkConnection = setInterval(() => {
                if (socket.connected) {
                  clearInterval(checkConnection);
                  resolve(true);
                }
              }, 100);
            }),
            new Promise((resolve) => setTimeout(() => resolve(false), 5000)),
          ]);

          if (!connected) {
            throw new Error("Socket connection timeout");
          }
        }

        console.log(`Joining channel ${selectedChannel}...`);

        // Call backend API to join channel
        const result = await pttApi.joinChannel(selectedChannel);
        console.log("Backend join result:", result);

        if (result.success) {
          // Emit socket event to join channel
          socket.emit("join_channel", selectedChannel);

          // Update connection state
          setIsConnected(true);
          setChannelConnected(true);
          setCurrentChannelId(selectedChannel);

          addSystemMessage(`✓ Connected to channel ${selectedChannel}`);

          // Load channel data
          try {
            await Promise.all([
              loadChannelMessages(selectedChannel),
              loadOnlineUsers(selectedChannel),
            ]);
          } catch (loadError) {
            console.warn("Error loading channel data:", loadError);
            addSystemMessage("Warning: Some channel data may not be available");
          }
        } else {
          throw new Error(result.error || "Failed to join channel");
        }
      } catch (error) {
        console.error("Connection error:", error);

        // Attempt graceful degradation - still connect via socket
        if (socket.connected) {
          console.log("Attempting socket-only connection...");
          socket.emit("join_channel", selectedChannel);
          setIsConnected(true);
          setChannelConnected(true);
          setCurrentChannelId(selectedChannel);
          addSystemMessage(
            `⚠ Connected to channel ${selectedChannel} (limited functionality: ${error.message})`
          );
        } else {
          addSystemMessage(`✗ Connection failed: ${error.message}`);
          setIsConnected(false);
          setChannelConnected(false);
        }
      }
    } else {
      // ✅ 4. Disconnect from current channel
      try {
        console.log(`Disconnecting from channel ${currentChannelId}...`);

        // Stop any active recording
        if (isPushToTalk || isRecording) {
          stopPushToTalk();
          await new Promise((resolve) => setTimeout(resolve, 500));
        }

        /*
        // Leave channel via API
        try {
          await pttApi.leaveChannel(currentChannelId);
          console.log("Successfully left channel via API");
        } catch (e) {
          console.warn("Leave channel API warning:", e);
        }
        */

        // Emit socket leave event
        if (socket.connected) {
          socket.emit("leave_channel", currentChannelId);
        }

        // Update state
        setIsConnected(false);
        setChannelConnected(false);
        setCurrentChannelId(null);
        setCurrentSpeaker(null);

        // Optionally disconnect socket entirely
        // socket.disconnect();

        addSystemMessage("✓ Disconnected from channel");
      } catch (error) {
        console.error("Disconnect error:", error);
        addSystemMessage(`⚠ Disconnect warning: ${error.message}`);

        // Force state cleanup even on error
        setIsConnected(false);
        setChannelConnected(false);
        setCurrentChannelId(null);
      }
    }
  };

  // Add these new functions:
  const loadChannelMessages = async (channelId) => {
    try {
      const result = await pttApi.getChannelMessages(channelId, 50);
      if (result.success && result.data) {
        // Transform backend messages to your format
        const formattedMessages = result.data.map((msg) => ({
          id: msg.id,
          type: msg.type,
          sender: msg.sender?.sr || "Unknown",
          content: msg.textContent || msg.audioUrl,
          timestamp: new Date(msg.created_at),
          duration: msg.audioDuration
            ? formatDuration(msg.audioDuration)
            : null,
          audioUrl: msg.audioUrl,
        }));

        setMessages(formattedMessages); // This REPLACES all messages with channel-specific ones
      }
    } catch (error) {
      console.error("Error loading messages:", error);
    }
  };

  const loadOnlineUsers = async (channelId) => {
    try {
      const result = await pttApi.getChannelOnlineUsers(channelId);
      if (result.success && result.data) {
        const formattedUsers = result.data.map((userInfo) => ({
          id: userInfo.infinityCode,
          name: userInfo.name,
          avatar: getRandomAvatar(),
          status: userInfo.isTalking ? "talking" : "online",
          permission:
            userInfo.role === "admin"
              ? "allowed"
              : userInfo.canSpeak
                ? "allowed"
                : "blocked",
          joinTime: new Date(userInfo.joinedAt),
          lastActivity: new Date(userInfo.lastSeen),
          isMuted: userInfo.isMuted,
          role: userInfo.role,
        }));

        setConnectedUsers(formattedUsers);
        console.log(
          `✅ Loaded ${formattedUsers.length} online users for channel ${channelId}`
        );
      }
    } catch (error) {
      console.error("Error loading online users:", error);
    }
  };
  /*
  const handleCreateChannel = async () => {
    if (!newChannelName.trim()) {
      alert("Channel name is required");
      return;
    }

    try {
      const channelData = {
        name: newChannelName,
        description: newChannelDescription,
        type: newChannelType,
        isPrivate: isChannelPrivate,
        memberIds: selectedMembers, // Include selected member IDs
      };

      const result = await pttApi.createChannel(channelData);
      if (result.success) {
        setSubscribedChannels((prev) => [...prev, result.data]);
        setShowCreateChannelModal(false);
        setNewChannelName("");
        setNewChannelDescription("");
        setSelectedMembers([]);
        setSearchQuery("");
        addSystemMessage(
          `✓ Channel "${result.data.name}" created successfully`
        );
      }
    } catch (error) {
      console.error("Error creating channel:", error);
      alert(`Failed to create channel: ${error.message}`);
    }
  };
  */

  const loadAllOnlineUsers = async () => {
    try {
      const result = await pttApi.getAllOnlineUsers();
      if (result.success && result.data) {
        setAllOnlineUsers(result.data);
      }
    } catch (error) {
      console.error("Error loading online users:", error);
    }
  };

  const handleStartDirectChat = async (targetUserCode) => {
    try {
      const result = await pttApi.createDirectChannel(targetUserCode);
      if (result.success && result.data) {
        // Add channel if not already in list
        const channelExists = subscribedChannels.some(
          (ch) => ch.id === result.data.id
        );
        if (!channelExists) {
          setSubscribedChannels((prev) => [...prev, result.data]);
        }

        // Select and connect to this channel
        setSelectedChannel(result.data.id);
        setShowOnlineUsersModal(false);

        // Auto-connect if not already connected
        if (!isConnected || currentChannelId !== result.data.id) {
          setTimeout(() => toggleConnection(), 100);
        }

        addSystemMessage(`✓ Direct chat with ${result.data.name} is ready`);
      }
    } catch (error) {
      console.error("Error creating direct channel:", error);
      alert(`Failed to start direct chat: ${error.message}`);
    }
  };

  // MODIFIED: startPushToTalk with WebRTC
  // MODIFIED: startPushToTalk with WebRTC
  const startPushToTalk = async () => {
    console.log("Starting push to talk...");
    if (!isConnected) {
      alert("يجب الاتصال بالشبكة أولاً");
      return;
    }
    if (!audioPermissions.granted) {
      alert("يجب السماح بالوصول للميكروفون أولاً");
      return;
    }

    const socket = socketRef.current;
    if (socket && socket.connected) {
      console.log("Emitting start_talking...");
      socket.emit("start_talking", { channelId: currentChannelId });
    }

    try {
      // Use WebRTC if enabled and available
      if (useWebRTC && webrtcManagerRef.current) {
        console.log("🚀 Using WebRTC for transmission");

        const success = await webrtcManagerRef.current.startTransmission(
          currentChannelId,
          volume / 100
        );

        if (success) {
          setIsPushToTalk(true);
          setIsRecording(true);
          setRecordingStartTime(Date.now());
          setCurrentSpeaker(localStorage.getItem("employeeName") || "أنت");
          return;
        } else {
          console.warn("⚠️ WebRTC failed, falling back to Socket.IO");
          setUseWebRTC(false); // Disable for this session
        }
      }

      // FALLBACK: Use existing Socket.IO method
      console.log("📡 Using Socket.IO fallback for transmission");

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      const mimeType = "audio/webm";
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: mimeType,
        audioBitsPerSecond: 128000,
      });

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);

          const socket = socketRef.current;
          if (socket && socket.connected) {
            socket.emit("audio_data", {
              channelId: currentChannelId,
              audioData: event.data,
            });
          }
        }
      };

      mediaRecorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start(100);

      setIsPushToTalk(true);
      setIsRecording(true);
      setRecordingStartTime(Date.now());
      setCurrentSpeaker(localStorage.getItem("employeeName") || "أنت");

      console.log("Recording started successfully (Socket.IO fallback)");
    } catch (error) {
      console.error("خطأ في بدء التسجيل:", error);
      alert("فشل في بدء التسجيل");
    }
  };

  // MODIFIED: stopPushToTalk with WebRTC
  // MODIFIED: stopPushToTalk with proper voice message saving
  const stopPushToTalk = async () => {
    console.log("Stopping push to talk...");

    const socket = socketRef.current;
    if (socket && socket.connected) {
      console.log("Emitting stop_talking...");
      socket.emit("stop_talking", { channelId: currentChannelId });
    }

    // Stop WebRTC transmission if active
    if (
      useWebRTC &&
      webrtcManagerRef.current &&
      webrtcManagerRef.current.currentChannelId
    ) {
      // Get recorded audio chunks from WebRTC manager
      const audioData = await webrtcManagerRef.current.stopTransmission(
        currentChannelId
      );

      if (audioData && audioData.blob) {
        // Upload the recorded audio as a voice message
        try {
          const duration = recordingTime * 1000;
          console.log("Uploading WebRTC voice message to backend...");

          const result = await pttApi.uploadVoiceMessage(
            currentChannelId,
            audioData.blob,
            duration
          );

          console.log("WebRTC voice message uploaded:", result);

          if (result.success && socket && socket.connected) {
            socket.emit("broadcast_voice_message", {
              id: result.data.id,
              audioUrl: result.data.audioUrl,
              duration: formatDuration(result.data.audioDuration),
              timestamp: result.data.created_at,
              channelId: currentChannelId,
            });
          }
        } catch (error) {
          console.error("Error uploading WebRTC voice message:", error);
          addSystemMessage(`Failed to upload voice: ${error.message}`);
        }
      }

      setIsPushToTalk(false);
      setIsRecording(false);
      setCurrentSpeaker(null);
      return;
    }

    // Fallback Socket.IO stop logic (existing code)
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsPushToTalk(false);
      setIsRecording(false);
      setCurrentSpeaker(null);

      const duration = recordingTime * 1000;

      try {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });

        console.log("Uploading Socket.IO voice message to backend...");
        const result = await pttApi.uploadVoiceMessage(
          currentChannelId,
          audioBlob,
          duration
        );
        console.log("Socket.IO voice message uploaded:", result);

        if (result.success && socket && socket.connected) {
          socket.emit("broadcast_voice_message", {
            id: result.data.id,
            audioUrl: result.data.audioUrl,
            duration: formatDuration(result.data.audioDuration),
            timestamp: result.data.created_at,
            channelId: currentChannelId,
          });
        }
      } catch (error) {
        console.error("Error uploading voice message:", error);
        addSystemMessage(`Failed to upload voice: ${error.message}`);
      }
    }
  };

  // PTT button handlers
  const handlePTTClick = () => {
    if (talkMode === "toggle") {
      if (isPushToTalk) {
        stopPushToTalk();
      } else {
        startPushToTalk();
      }
    }
  };

  const handlePTTMouseDown = (e) => {
    e.preventDefault();
    if (talkMode === "hold" && !isPushToTalk) {
      startPushToTalk();
    }
  };

  const handlePTTMouseUp = (e) => {
    e.preventDefault();
    if (talkMode === "hold" && isPushToTalk) {
      stopPushToTalk();
    }
  };

  const handlePTTMouseLeave = (e) => {
    if (talkMode === "hold" && isPushToTalk) {
      stopPushToTalk();
    }
  };

  // User management functions
  const updateUserPermission = (userId, permission) => {
    setConnectedUsers((prev) =>
      prev.map((user) =>
        user.id === userId
          ? { ...user, permission, lastActivity: new Date() }
          : user
      )
    );

    const user = connectedUsers.find((u) => u.id === userId);
    if (user) {
      addSystemMessage(`${user.name} permission changed to ${permission}`);
    }
  };
  // Add WebRTC toggle and stats to your debug panel
  const renderDebugPanel = () => (
    <div style={styles.debugInfo}>
      <div style={{ marginBottom: "8px", fontWeight: "600" }}>
        System Status
      </div>

      <div style={styles.debugItem}>
        <span>Socket:</span>
        <span
          style={{
            color: socketConnected ? "#10b981" : "#ef4444",
            fontWeight: "600",
          }}
        >
          {socketConnected ? "✓" : "✗"}
        </span>
      </div>

      <div style={styles.debugItem}>
        <span>Channel:</span>
        <span
          style={{
            color: channelConnected ? "#10b981" : "#ef4444",
            fontWeight: "600",
          }}
        >
          {channelConnected ? "✓" : "✗"}
        </span>
      </div>

      {/* WebRTC Toggle */}
      <div
        style={{
          ...styles.debugItem,
          cursor: "pointer",
          background: useWebRTC
            ? "rgba(16,185,129,0.1)"
            : "rgba(239,68,68,0.1)",
          padding: "4px 8px",
          borderRadius: "4px",
          marginTop: "8px",
        }}
        onClick={() => setUseWebRTC(!useWebRTC)}
      >
        <span>WebRTC:</span>
        <span
          style={{
            color: useWebRTC ? "#10b981" : "#ef4444",
            fontWeight: "600",
          }}
        >
          {useWebRTC ? "ON" : "OFF"}
        </span>
      </div>

      {/* WebRTC Stats */}
      {useWebRTC && webrtcStats && (
        <>
          <div style={styles.debugItem}>
            <span>Packets Lost:</span>
            <span style={{ fontWeight: "600" }}>
              {webrtcStats.packetsLost || 0}
            </span>
          </div>

          <div style={styles.debugItem}>
            <span>Jitter:</span>
            <span style={{ fontWeight: "600" }}>
              {webrtcStats.jitter ? (webrtcStats.jitter * 1000).toFixed(1) : 0}
              ms
            </span>
          </div>
        </>
      )}

      <div style={styles.debugItem}>
        <span>Recording:</span>
        <span
          style={{
            color: isRecording ? "#ef4444" : "#6b7280",
            fontWeight: "600",
          }}
        >
          {isRecording ? "🔴" : "⚫"}
        </span>
      </div>

      <div style={styles.debugItem}>
        <span>Microphone:</span>
        <span
          style={{
            color: audioPermissions.granted ? "#10b981" : "#ef4444",
            fontWeight: "600",
          }}
        >
          {audioPermissions.granted ? "✓" : "✗"}
        </span>
      </div>
    </div>
  );

  const kickUser = (userId) => {
    const user = connectedUsers.find((u) => u.id === userId);
    if (user) {
      setConnectedUsers((prev) => prev.filter((u) => u.id !== userId));
      addSystemMessage(`${user.name} has been removed from the channel`);

      const socket = socketRef.current;
      if (socket && socket.connected) {
        socket.emit("kick_user", { userId, channelId: selectedChannel });
      }
    }
  };

  // Message functions
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageMessage = {
          id: Date.now(),
          type: "image",
          sender: localStorage.getItem("employeeName") || "You",
          content: "Image",
          timestamp: new Date(),
          imageUrl: e.target.result,
        };
        setMessages((prev) => [...prev, imageMessage]);
      };
      reader.readAsDataURL(file);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !currentChannelId) return;

    const textMessage = {
      id: Date.now(),
      type: "text",
      sender: localStorage.getItem("employeeName") || "You",
      content: newMessage,
      timestamp: new Date(),
      channelId: currentChannelId,
    };

    // Optimistic update
    setMessages((prev) => [...prev, textMessage]);
    setNewMessage("");

    try {
      // Send to backend API
      const result = await pttApi.sendTextMessage(currentChannelId, newMessage);
      console.log("Message sent to backend:", result);

      // Also send via socket for real-time
      const socket = socketRef.current;
      if (socket && socket.connected) {
        socket.emit("send_message", {
          channelId: currentChannelId,
          message: newMessage,
        });
      }
    } catch (error) {
      console.error("Error sending message:", error);
      addSystemMessage(`Failed to send message: ${error.message}`);
    }
  };
  // Utility functions
  const getUserStatusColor = (status) => {
    const colors = {
      online: "#10b981",
      talking: "#ef4444",
      offline: "#6b7280",
    };
    return colors[status] || "#6b7280";
  };

  const getPermissionColor = (permission) => {
    const colors = {
      allowed: "#10b981",
      pending: "#f59e0b",
      blocked: "#ef4444",
    };
    return colors[permission] || "#6b7280";
  };

  const getPermissionButtonStyle = (permission, type) => {
    const baseStyle = {
      width: "32px",
      height: "32px",
      borderRadius: "8px",
      border: "none",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.3s ease",
      fontSize: "12px",
    };

    if (permission === type) {
      const colors = {
        allowed: {
          background: "linear-gradient(135deg, #10b981, #059669)",
          color: "#fff",
          boxShadow: "0 2px 8px rgba(16,185,129,0.3)",
        },
        pending: {
          background: "linear-gradient(135deg, #f59e0b, #d97706)",
          color: "#fff",
          boxShadow: "0 2px 8px rgba(245,158,11,0.3)",
        },
        blocked: {
          background: "linear-gradient(135deg, #ef4444, #dc2626)",
          color: "#fff",
          boxShadow: "0 2px 8px rgba(239,68,68,0.3)",
        },
      };
      return { ...baseStyle, ...colors[type] };
    }

    return {
      ...baseStyle,
      background:
        theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
      color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(26,26,26,0.7)",
    };
  };

  const getSignalIcon = () => {
    if (signalStrength >= 80) return <SignalHigh size={16} color="#10b981" />;
    if (signalStrength >= 50) return <Signal size={16} color="#f59e0b" />;
    return <SignalLow size={16} color="#ef4444" />;
  };

  // Get user statistics
  const getUserStats = () => {
    const total = connectedUsers.length;
    const online = connectedUsers.filter((u) => u.status !== "offline").length;
    const allowed = connectedUsers.filter(
      (u) => u.permission === "allowed"
    ).length;
    const pending = connectedUsers.filter(
      (u) => u.permission === "pending"
    ).length;
    const blocked = connectedUsers.filter(
      (u) => u.permission === "blocked"
    ).length;

    return { total, online, allowed, pending, blocked };
  };

  const stats = getUserStats();

  // Enhanced styles with improved sidebar
  const styles = {
    container: {
      marginTop: '-30px',
    },

    header: {
      background:
        theme === "dark"
          ? "linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))"
          : "linear-gradient(135deg, rgba(255,255,255,0.95), rgba(255,255,255,0.85))",
      padding: isMobile ? "12px 16px" : isTablet ? "16px 20px" : "20px 28px",
      borderRadius: "16px",
      marginBottom: "16px",
      boxShadow:
        theme === "dark"
          ? "0 10px 30px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)"
          : "0 10px 30px rgba(0,0,0,0.12)",
      backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.12)"
          : "1px solid rgba(0,0,0,0.08)",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexShrink: 0,
    },

    headerContent: {
      flex: 1,
    },

    welcomeTitle: {
      fontSize: isMobile ? "18px" : "24px",
      fontWeight: "800",
      color: theme === "dark" ? "#fff" : "#1a1a1a",
      margin: "0 0 4px 0",
      letterSpacing: "-0.02em",
    },

    welcomeSubtitle: {
      fontSize: isMobile ? "12px" : "14px",
      color: theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(26,26,26,0.7)",
      fontWeight: "500",
    },

    messageButton: {
      padding: "12px 18px",
      borderRadius: "12px",
      border: "none",
      background: "linear-gradient(135deg, #3b82f6, #2563eb)",
      color: "#fff",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      transition: "all 0.3s ease",
      fontWeight: "600",
      fontSize: "14px",
      boxShadow: "0 4px 15px rgba(59,130,246,0.3)",
    },

    mainContent: {
      flex: 1,
      display: "grid",
      gridTemplateColumns: isMobile
        ? "1fr" // شاشة موبايل: عمود واحد فقط
        : isTablet
          ? "1fr 1fr" // شاشة تابلت: عمودين
          : "280px 1fr 320px", // كمبيوتر: 3 أعمدة
      gap: "16px",
      overflow: "hidden",
      marginLeft: 25,
    },


    // Left panel - Connection controls
    connectionPanel: {
      background:
        theme === "dark"
          ? "linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))"
          : "linear-gradient(135deg, rgba(255,255,255,0.95), rgba(255,255,255,0.85))",
      padding: "20px",
      borderRadius: "16px",
      boxShadow:
        theme === "dark"
          ? "0 10px 30px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)"
          : "0 10px 30px rgba(0,0,0,0.12)",
      backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.12)"
          : "1px solid rgba(0,0,0,0.08)",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },

    // Center panel - Push-to-talk
    pttSection: {
      background:
        theme === "dark"
          ? "linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))"
          : "linear-gradient(135deg, rgba(255,255,255,0.95), rgba(255,255,255,0.85))",
      padding: isMobile ? "20px" : isTablet ? "28px" : "32px",
      borderRadius: "16px",
      boxShadow:
        theme === "dark"
          ? "0 15px 40px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.1)"
          : "0 15px 40px rgba(0,0,0,0.15)",
      backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.15)"
          : "1px solid rgba(0,0,0,0.08)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "20px",
      position: "relative",
      overflow: "hidden",
    },

    // Right panel - Users sidebar (Enhanced)
    usersSidebar: {
      background:
        theme === "dark"
          ? "linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))"
          : "linear-gradient(135deg, rgba(255,255,255,0.95), rgba(255,255,255,0.85))",
      padding: "20px",
      borderRadius: "16px",
      boxShadow:
        theme === "dark"
          ? "0 10px 30px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)"
          : "0 10px 30px rgba(0,0,0,0.12)",
      backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.12)"
          : "1px solid rgba(0,0,0,0.08)",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },

    cardTitle: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      marginBottom: "16px",
      fontSize: "16px",
      fontWeight: "700",
      color: theme === "dark" ? "#fff" : "#1a1a1a",
    },

    // Sidebar header with stats
    sidebarHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "16px",
      padding: "12px 16px",
      borderRadius: "12px",
      background:
        theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
    },

    sidebarTitle: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "16px",
      fontWeight: "700",
      color: theme === "dark" ? "#fff" : "#1a1a1a",
    },

    onlineIndicator: {
      width: "8px",
      height: "8px",
      borderRadius: "50%",
      background: "#10b981",
      animation: "pulse 2s infinite",
    },

    // Auto-manage toggle
    autoManageToggle: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "8px 12px",
      borderRadius: "8px",
      background: autoManageUsers
        ? "linear-gradient(135deg, #10b981, #059669)"
        : theme === "dark"
          ? "rgba(255,255,255,0.1)"
          : "rgba(0,0,0,0.1)",
      color: autoManageUsers
        ? "#fff"
        : theme === "dark"
          ? "rgba(255,255,255,0.7)"
          : "rgba(26,26,26,0.7)",
      cursor: "pointer",
      fontSize: "12px",
      fontWeight: "600",
      transition: "all 0.3s ease",
      marginBottom: "12px",
    },

    // Users list container
    usersContainer: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },

    usersList: {
      flex: 1,
      overflowY: "auto",
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      paddingRight: "4px",
    },

    // Individual user item (Enhanced)
    userItem: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "12px",
      borderRadius: "12px",
      background:
        theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
      transition: "all 0.3s ease",
      cursor: "pointer",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.12)"
          : "1px solid rgba(0,0,0,0.08)",
      position: "relative",
      marginBottom: "5%",
    },

    selectedUserItem: {
      background:
        theme === "dark" ? "rgba(59,130,246,0.2)" : "rgba(59,130,246,0.1)",
      border: "1px solid rgba(59,130,246,0.4)",
      boxShadow: "0 2px 8px rgba(59,130,246,0.2)",
    },

    avatar: {
      width: "32px",
      height: "32px",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "14px",
      background:
        "linear-gradient(135deg, rgba(59,130,246,0.3), rgba(59,130,246,0.1))",
      border: "2px solid rgba(59,130,246,0.4)",
      position: "relative",
    },

    statusBadge: {
      position: "absolute",
      bottom: "-2px",
      right: "-2px",
      width: "10px",
      height: "10px",
      borderRadius: "50%",
      border: "2px solid white",
    },

    userInfo: {
      flex: 1,
      minWidth: 0,
    },

    userName: {
      fontSize: "13px",
      fontWeight: "700",
      color: theme === "dark" ? "#fff" : "#1a1a1a",
      marginBottom: "2px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },

    userStatus: {
      fontSize: "10px",
      color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(26,26,26,0.7)",
      display: "flex",
      alignItems: "center",
      gap: "4px",
    },

    userMeta: {
      fontSize: "9px",
      color: theme === "dark" ? "rgba(255,255,255,0.5)" : "rgba(26,26,26,0.5)",
      marginTop: "2px",
    },

    // Permission controls (Improved)
    permissionControls: {
      display: "flex",
      flexDirection: "column",
      gap: "2px",
    },

    permissionButtons: {
      display: "flex",
      gap: "4px",
    },

    permissionButton: {
      width: "28px",
      height: "28px",
      borderRadius: "6px",
      border: "none",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.3s ease",
      fontSize: "10px",
    },

    // Connection status section
    connectionStatus: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "12px 16px",
      borderRadius: "12px",
      marginBottom: "16px",
      background: isConnected
        ? "linear-gradient(135deg, rgba(16,185,129,0.2), rgba(16,185,129,0.05))"
        : "linear-gradient(135deg, rgba(239,68,68,0.2), rgba(239,68,68,0.05))",
      border: isConnected
        ? "1px solid rgba(16,185,129,0.4)"
        : "1px solid rgba(239,68,68,0.4)",
    },

    statusIndicator: {
      width: "10px",
      height: "10px",
      borderRadius: "50%",
      background: isConnected
        ? "radial-gradient(circle, #10b981 0%, #059669 100%)"
        : "radial-gradient(circle, #ef4444 0%, #dc2626 100%)",
      boxShadow: isConnected
        ? "0 0 12px rgba(16,185,129,0.8)"
        : "0 0 12px rgba(239,68,68,0.8)",
      animation: isConnected ? "pulse 2s infinite" : "none",
    },

    statusText: {
      color: theme === "dark" ? "#fff" : "#1a1a1a",
      fontWeight: "600",
      fontSize: "12px",
      flex: 1,
    },

    connectButton: {
      padding: "8px 12px",
      borderRadius: "8px",
      border: "none",
      background: isConnected
        ? "linear-gradient(135deg, #ef4444, #dc2626)"
        : "linear-gradient(135deg, #10b981, #059669)",
      color: "#fff",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "6px",
      transition: "all 0.3s ease",
      fontWeight: "600",
      fontSize: "11px",
    },

    // Mode selector
    modeSelector: {
      display: "flex",
      gap: "6px",
      padding: "4px",
      borderRadius: "12px",
      background:
        theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
      marginBottom: "16px",
    },

    modeButton: {
      padding: "8px 14px",
      borderRadius: "8px",
      border: "none",
      cursor: "pointer",
      fontSize: "15px",
      fontWeight: "600",
      transition: "all 0.3s ease",
    },

    activeModeButton: {
      background: "linear-gradient(135deg, #10b981, #059669)",
      color: "#fff",
      boxShadow: "0 2px 8px rgba(16,185,129,0.3)",
    },

    inactiveModeButton: {
      background: "transparent",
      color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(26,26,26,0.7)",
    },

    // Current speaker indicator
    currentSpeaker: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "12px 16px",
      borderRadius: "12px",
      background:
        "linear-gradient(135deg, rgba(16,185,129,0.2), rgba(16,185,129,0.05))",
      border: "2px solid rgba(16,185,129,0.4)",
      fontSize: "12px",
      color: theme === "dark" ? "#fff" : "#1a1a1a",
      fontWeight: "600",
      marginBottom: "12px",
    },

    // Recording timer
    recordingTimer: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "10px 14px",
      borderRadius: "12px",
      background:
        "linear-gradient(135deg, rgba(239,68,68,0.2), rgba(239,68,68,0.05))",
      border: "2px solid rgba(239,68,68,0.4)",
      fontSize: "13px",
      fontWeight: "700",
      color: "#ef4444",
      animation: "pulse 1s infinite",
      marginBottom: "12px",
    },

    // PTT Button
    pttButton: {
      position: "relative",
      width: isMobile ? "120px" : "160px",
      height: isMobile ? "120px" : "160px",
      fontSize: isMobile ? "10px" : "12px",
      borderRadius: "50%",
      border: "none",
      background: isPushToTalk
        ? "linear-gradient(135deg, #ef4444, #dc2626)"
        : isConnected
          ? "linear-gradient(135deg, #10b981, #059669)"
          : "linear-gradient(135deg, #6b7280, #4b5563)",
      color: "#fff",
      cursor: isConnected ? "pointer" : "not-allowed",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      fontWeight: "800",
      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
      opacity: isConnected ? 1 : 0.6,
      transform: isPushToTalk ? "scale(1.05)" : "scale(1)",
      boxShadow: isPushToTalk
        ? "0 0 40px rgba(239,68,68,0.9), 0 10px 30px rgba(239,68,68,0.6)"
        : isConnected
          ? "0 10px 30px rgba(16,185,129,0.6)"
          : "0 10px 30px rgba(107,114,128,0.4)",
      userSelect: "none",
    },

    pttInstructions: {
      fontSize: "12px",
      color:
        theme === "dark" ? "rgba(255,255,255,0.85)" : "rgba(26,26,26,0.85)",
      textAlign: "center",
      fontWeight: "500",
      lineHeight: "1.4",
      maxWidth: "280px",
    },

    // Volume control
    volumeControl: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "12px 16px",
      borderRadius: "12px",
      background:
        theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
      width: "100%",
      maxWidth: "280px",
    },

    volumeSlider: {
      flex: 1,
      height: "4px",
      borderRadius: "2px",
      background:
        theme === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
      outline: "none",
      cursor: "pointer",
      appearance: "none",
    },

    // Network stats
    networkStats: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "10px 12px",
      borderRadius: "10px",
      background:
        theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
      marginBottom: "12px",
      fontSize: "11px",
    },
    channelSelector: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      marginBottom: "16px",
    },

    channelOption: {
      padding: "12px",
      borderRadius: "8px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      border: "2px solid transparent",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      background:
        theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
    },

    selectedChannelOption: {
      background:
        "linear-gradient(135deg, rgba(59,130,246,0.2), rgba(59,130,246,0.1))",
      border: "2px solid rgba(59,130,246,0.6)",
      boxShadow: "0 2px 8px rgba(59,130,246,0.3)",
    },

    channelActivity: {
      display: "flex",
      alignItems: "center",
      gap: "4px",
      fontSize: "10px",
      color: "#10b981",
    },

    channelActivityDot: {
      width: "8px",
      height: "8px",
      borderRadius: "50%",
      background: "#10b981",
      animation: "pulse 1s infinite",
    },

    // Statistics panel at bottom of sidebar
    statsPanel: {
      marginTop: "auto",
      padding: "12px",
      borderRadius: "12px",
      background:
        theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.12)"
          : "1px solid rgba(0,0,0,0.12)",
    },

    statsTitle: {
      fontSize: "12px",
      fontWeight: "700",
      color: theme === "dark" ? "#fff" : "#1a1a1a",
      marginBottom: "8px",
      display: "flex",
      alignItems: "center",
      gap: "6px",
    },

    statsGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "8px",
    },

    statItem: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "4px 8px",
      borderRadius: "6px",
      background:
        theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
    },

    statLabel: {
      fontSize: "10px",
      color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(26,26,26,0.7)",
      fontWeight: "500",
    },

    statValue: {
      fontSize: "11px",
      fontWeight: "700",
    },

    // Debug info
    debugInfo: {
      marginTop: "auto",
      padding: "12px",
      borderRadius: "12px",
      background:
        theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
      fontSize: "10px",
      color: theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(26,26,26,0.8)",
      fontFamily: "'JetBrains Mono', 'SF Mono', 'Monaco', monospace",
    },

    debugItem: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "3px 0",
      borderBottom:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.08)"
          : "1px solid rgba(0,0,0,0.08)",
    },

    modal: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0,0,0,0.75)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      backdropFilter: "blur(12px)",
      WebkitBackdropFilter: "blur(12px)",
      animation: "fadeIn 0.3s ease",
    },

    modalContent: {
      background:
        theme === "dark"
          ? "linear-gradient(145deg, rgba(30,30,35,0.98), rgba(20,20,25,0.98))"
          : "linear-gradient(145deg, rgba(255,255,255,0.98), rgba(250,250,252,0.98))",
      borderRadius: "24px",
      padding: "32px",
      maxWidth: "600px",
      width: "90%",
      maxHeight: "85vh",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      boxShadow:
        theme === "dark"
          ? "0 30px 70px rgba(0,0,0,0.9), 0 0 0 1px rgba(255,255,255,0.15), inset 0 1px 0 rgba(255,255,255,0.1)"
          : "0 30px 70px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.05)",
      backdropFilter: "blur(40px)",
      WebkitBackdropFilter: "blur(40px)",
      transform: "scale(1)",
      animation: "modalSlideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
      scrollY: "auto"
    },

    modalHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "16px",
      paddingBottom: "12px",
      borderBottom:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.15)"
          : "1px solid rgba(0,0,0,0.15)",
    },

    modalTitle: {
      fontSize: "18px",
      fontWeight: "700",
      color: theme === "dark" ? "#fff" : "#1a1a1a",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },

    closeButton: {
      width: "36px",
      height: "36px",
      borderRadius: "12px",
      border: "none",
      background:
        theme === "dark" ? "rgba(239,68,68,0.15)" : "rgba(239,68,68,0.1)",
      color: "#ef4444",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.3s ease",
      "&:hover": {
        background: "linear-gradient(135deg, #ef4444, #dc2626)",
        color: "#fff",
        transform: "rotate(90deg)",
      },
    },
    messageAnimated: {
      display: "flex",
      flexDirection: "column",
      gap: "6px",
      padding: "14px 18px",
      borderRadius: "16px",
      maxWidth: "80%",
      background:
        theme === "dark"
          ? "linear-gradient(135deg, rgba(255,255,255,0.12), rgba(255,255,255,0.08))"
          : "linear-gradient(135deg, rgba(255,255,255,0.95), rgba(250,250,252,0.95))",
      backdropFilter: "blur(15px)",
      WebkitBackdropFilter: "blur(15px)",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.15)"
          : "1px solid rgba(0,0,0,0.08)",
      boxShadow:
        theme === "dark"
          ? "0 4px 12px rgba(0,0,0,0.3)"
          : "0 4px 12px rgba(0,0,0,0.08)",
      transition: "all 0.3s ease",
      animation: "slideIn 0.4s ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow:
          theme === "dark"
            ? "0 6px 16px rgba(0,0,0,0.4)"
            : "0 6px 16px rgba(0,0,0,0.12)",
      },
    },

    messagesContainer: {
      flex: 1,
      overflowY: "auto",
      padding: "16px",
      display: "flex",
      flexDirection: "column",
      gap: "12px",
    },

    message: {
      display: "flex",
      flexDirection: "column",
      gap: "6px",
      padding: "12px 16px",
      borderRadius: "12px",
      maxWidth: "80%",
      background:
        theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)",
      backdropFilter: "blur(15px)",
      WebkitBackdropFilter: "blur(15px)",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.12)"
          : "1px solid rgba(0,0,0,0.08)",
      transition: "all 0.3s ease",
    },

    messageHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      fontSize: "10px",
      color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(26,26,26,0.7)",
      fontWeight: "600",
    },

    messageContent: {
      fontSize: "12px",
      color: theme === "dark" ? "#fff" : "#1a1a1a",
      lineHeight: "1.4",
      fontWeight: "500",
    },

    voiceMessage: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "4px",
    },

    playButton: {
      width: "36px",
      height: "36px",
      borderRadius: "50%",
      border: "none",
      background: "linear-gradient(135deg, #3b82f6, #2563eb)",
      color: "#fff",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      boxShadow: "0 4px 12px rgba(59,130,246,0.4)",
      position: "relative",
      overflow: "hidden",
    },

    messageImage: {
      maxWidth: "200px",
      borderRadius: "8px",
      cursor: "pointer",
      transition: "all 0.3s ease",
    },

    systemMessage: {
      alignSelf: "center",
      maxWidth: "none",
      background:
        theme === "dark" ? "rgba(16,185,129,0.2)" : "rgba(16,185,129,0.1)",
      border: "1px solid rgba(16,185,129,0.4)",
      color: "#10b981",
      fontStyle: "italic",
      textAlign: "center",
      fontSize: "11px",
      fontWeight: "600",
    },

    inputContainer: {
      display: "flex",
      gap: "8px",
      padding: "16px",
      borderTop:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.12)"
          : "1px solid rgba(0,0,0,0.12)",
      background:
        theme === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)",
    },

    messageInput: {
      flex: 1,
      padding: "10px 14px",
      borderRadius: "12px",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.2)"
          : "1px solid rgba(0,0,0,0.2)",
      background:
        theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.9)",
      color: theme === "dark" ? "#fff" : "#1a1a1a",
      outline: "none",
      fontSize: "12px",
      transition: "all 0.3s ease",
      fontWeight: "500",
    },

    actionButton: {
      width: "40px",
      height: "40px",
      borderRadius: "12px",
      border: "none",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.3s ease",
    },
    // Add to existing styles object (around line 1200):

    headerButtons: {
      display: "flex",
      gap: "12px",
      alignItems: "center",
    },

    channelInfo: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "8px 12px",
      borderRadius: "8px",
      background:
        theme === "dark" ? "rgba(59,130,246,0.15)" : "rgba(59,130,246,0.1)",
      border: `1px solid rgba(59,130,246,0.3)`,
      fontSize: "11px",
      fontWeight: "600",
      color: theme === "dark" ? "#60a5fa" : "#2563eb",
    },

    messageTimestamp: {
      fontSize: "9px",
      opacity: 0.6,
      fontStyle: "italic",
      marginTop: "4px",
    },

    unreadBadge: {
      background: "linear-gradient(135deg, #ef4444, #dc2626)",
      color: "#fff",
      borderRadius: "12px",
      padding: "2px 8px",
      fontSize: "10px",
      fontWeight: "700",
      minWidth: "20px",
      textAlign: "center",
    },

    pulse: {
      animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
    },

    playButtonActive: {
      width: "28px",
      height: "28px",
      borderRadius: "50%",
      border: "none",
      background: "linear-gradient(135deg, #ef4444, #dc2626)",
      color: "#fff",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.3s ease",
      animation: "pulse 1.5s infinite",
    },
    enhancedInput: {
      flex: 1,
      padding: "12px 16px",
      borderRadius: "12px",
      border:
        theme === "dark"
          ? "2px solid rgba(255,255,255,0.15)"
          : "2px solid rgba(0,0,0,0.15)",
      background:
        theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.9)",
      color: theme === "dark" ? "#fff" : "#1a1a1a",
      outline: "none",
      fontSize: "13px",
      transition: "all 0.3s ease",
      fontWeight: "500",
      "&:focus": {
        border: "2px solid #3b82f6",
        boxShadow: "0 0 0 3px rgba(59,130,246,0.1)",
      },
    },
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div
        style={{
          ...styles.header,
          display: "flex",
          flexDirection:
            window.innerWidth <= 768 ? "column" : "row", // موبايل: عمودي / ديسكتوب: أفقي
          alignItems:
            window.innerWidth <= 768 ? "flex-start" : "center",
          justifyContent: "space-between",
          gap: window.innerWidth <= 768 ? "12px" : "0",
          flexWrap: "wrap",
          marginLeft: 25,
        }}
      >
        <div
          style={{
            ...styles.headerContent,
            display: "flex",
            flexDirection:
              window.innerWidth <= 768 ? "column" : "row",
            alignItems:
              window.innerWidth <= 768 ? "flex-start" : "center",
            gap: window.innerWidth <= 768 ? "6px" : "12px",
            flex: "1",
          }}
        >
          <h1
            style={{
              ...styles.welcomeTitle,
              fontSize:
                window.innerWidth <= 600
                  ? "16px"
                  : window.innerWidth <= 1024
                    ? "20px"
                    : "24px",
              textAlign:
                window.innerWidth <= 768 ? "left" : "center",
            }}
          >
            Push-to-Talk Communication System
          </h1>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flexWrap: "wrap",
            }}
          >


            {isConnected && currentChannelId && (
              <>
                <div
                  style={{
                    width: "4px",
                    height: "4px",
                    borderRadius: "50%",
                    background: "#6b7280",
                  }}
                />
                <div
                  style={{
                    ...styles.channelInfo,
                    fontSize:
                      window.innerWidth <= 600
                        ? "11px"
                        : "13px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    flexWrap: "wrap",
                  }}
                >
                  <Radio size={12} />
                  <span>
                    {subscribedChannels.find((ch) => ch.id === currentChannelId)
                      ?.name || `Channel ${currentChannelId}`}
                  </span>
                  {connectedUsers.length > 0 && (
                    <span style={{ opacity: 0.7 }}>
                      • {connectedUsers.length} online
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div
          style={{
            ...styles.headerButtons,
            display: "flex",
            flexWrap: "wrap",
            justifyContent:
              window.innerWidth <= 768
                ? "flex-start"
                : "flex-end",
            gap: window.innerWidth <= 768 ? "8px" : "12px",
            width: window.innerWidth <= 768 ? "100%" : "auto",
          }}
        >
          <button
            style={{
              ...styles.messageButton,
              flex:
                window.innerWidth <= 768
                  ? "1 1 45%"
                  : "none",
              fontSize:
                window.innerWidth <= 600
                  ? "11px"
                  : "13px",
              padding:
                window.innerWidth <= 600
                  ? "6px 10px"
                  : "8px 14px",
            }}
            onClick={() => setShowMessagesModal(true)}
          >
            <MessageSquare size={16} />
            Messages
            {messages.length > 0 && (
              <span style={styles.unreadBadge}>{messages.length}</span>
            )}
          </button>

          {/*<button
            style={{
              ...styles.messageButton,
              background:
                "linear-gradient(135deg, #8b5cf6, #7c3aed)",
              flex:
                window.innerWidth <= 768
                  ? "1 1 45%"
                  : "none",
              fontSize:
                window.innerWidth <= 600
                  ? "11px"
                  : "13px",
              padding:
                window.innerWidth <= 600
                  ? "6px 10px"
                  : "8px 14px",
            }}
            onClick={() => setShowCreateChannelModal(true)}
          >
            <Radio size={16} />
            Create
          </button>
          */}

          <button
            style={{
              ...styles.messageButton,
              background:
                "linear-gradient(135deg, #f59e0b, #d97706)",
              flex:
                window.innerWidth <= 768
                  ? "1 1 100%"
                  : "none",
              fontSize:
                window.innerWidth <= 600
                  ? "11px"
                  : "13px",
              padding:
                window.innerWidth <= 600
                  ? "6px 10px"
                  : "8px 14px",
            }}
            onClick={() => {
              loadAllOnlineUsers();
              setShowOnlineUsersModal(true);
            }}
          >
            <Users size={16} />
            Direct Messages
          </button>
        </div>
      </div>


      {/* Main Content */}
      <div style={styles.mainContent}>
        {/* Left Panel - Connection Controls */}
        <div style={styles.connectionPanel}>
          <h3 style={styles.cardTitle}>
            <Radio size={18} />
            Transmit Channel
          </h3>

          {/* Channel Selector */}
          <div style={styles.channelSelector}>
            {subscribedChannels.length === 0 ? (
              <div
                style={{
                  padding: "16px",
                  textAlign: "center",
                  color:
                    theme === "dark"
                      ? "rgba(255,255,255,0.6)"
                      : "rgba(26,26,26,0.6)",
                  fontSize: "12px",
                }}
              >
                No channels available
              </div>
            ) : (
              subscribedChannels.map((channel) => (
                <div
                  key={channel.id}
                  style={
                    selectedChannel === channel.id
                      ? {
                        ...styles.channelOption,
                        ...styles.selectedChannelOption,
                      }
                      : styles.channelOption
                  }
                  onClick={() => {
                    setSelectedChannel(channel.id);
                    if (isConnected && currentChannelId !== channel.id) {
                      toggleConnection();
                    } // This will trigger the switch logic
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontWeight: "600",
                        fontSize: "13px",
                        marginBottom: "2px",
                      }}
                    >
                      {channel.name}
                    </div>
                    <div style={{ fontSize: "10px", opacity: 0.7 }}>
                      {channel._count?.members || 0} members
                    </div>
                  </div>

                  {channelActivity[channel.id] && (
                    <div style={styles.channelActivity}>
                      <div style={styles.channelActivityDot} />
                      <span>{channelActivity[channel.id].speaker}</span>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Connection Status */}
          <div style={styles.connectionStatus}>
            <div style={styles.statusIndicator}></div>
            <span style={styles.statusText}>
              {isConnected ? "Connected" : "Disconnected"}
            </span>
            <button style={styles.connectButton} onClick={toggleConnection}>
              {isConnected ? <PhoneOff size={14} /> : <Phone size={14} />}
              {isConnected ? "Disconnect" : "Connect"}
            </button>
          </div>

          {/* Network Statistics */}
          <div style={styles.networkStats}>
            {socketConnected ? (
              <Wifi size={14} color="#10b981" />
            ) : (
              <WifiOff size={14} color="#ef4444" />
            )}
            <span style={{ fontWeight: "600" }}>{networkLatency}ms</span>
            {getSignalIcon()}
            <span style={{ fontWeight: "600" }}>{signalStrength}%</span>
          </div>

          {/* Debug Information */}
          <div style={styles.debugInfo}>
            <div style={{ marginBottom: "8px", fontWeight: "600" }}>
              System Status
            </div>

            <div style={styles.debugItem}>
              <span>Socket:</span>
              <span
                style={{
                  color: socketConnected ? "#10b981" : "#ef4444",
                  fontWeight: "600",
                }}
              >
                {socketConnected ? "✓" : "✗"}
              </span>
            </div>

            <div style={styles.debugItem}>
              <span>Channel:</span>
              <span
                style={{
                  color: channelConnected ? "#10b981" : "#ef4444",
                  fontWeight: "600",
                }}
              >
                {channelConnected ? "✓" : "✗"}
              </span>
            </div>

            <div style={styles.debugItem}>
              <span>Recording:</span>
              <span
                style={{
                  color: isRecording ? "#ef4444" : "#6b7280",
                  fontWeight: "600",
                }}
              >
                {isRecording ? "🔴" : "⚫"}
              </span>
            </div>

            <div style={styles.debugItem}>
              <span>Microphone:</span>
              <span
                style={{
                  color: audioPermissions.granted ? "#10b981" : "#ef4444",
                  fontWeight: "600",
                }}
              >
                {audioPermissions.granted ? "✓" : "✗"}
              </span>
            </div>
          </div>
        </div>
        {/* Create Channel Modal */}
        {showCreateChannelModal && (
          <div
            style={styles.modal}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowCreateChannelModal(false);
                setSearchQuery("");
                setSelectedMembers([]);
              }
            }}
          >
            <div style={{ ...styles.modalContent, maxWidth: "600px" }}>
              <div style={styles.modalHeader}>
                <h3 style={styles.modalTitle}>
                  <Radio size={18} />
                  Create New Channel
                </h3>
                <button
                  style={styles.closeButton}
                  onClick={() => {
                    setShowCreateChannelModal(false);
                    setSearchQuery("");
                    setSelectedMembers([]);
                  }}
                >
                  <X size={16} />
                </button>
              </div>

              <div
                style={{
                  padding: "16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                {/* Channel Name */}
                <div>
                  <label
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      marginBottom: "6px",
                      display: "block",
                    }}
                  >
                    Channel Name *
                  </label>
                  <input
                    type="text"
                    placeholder="Enter channel name"
                    value={newChannelName}
                    onChange={(e) => setNewChannelName(e.target.value)}
                    style={styles.messageInput}
                  />
                </div>

                {/* Description */}
                <div>
                  <label
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      marginBottom: "6px",
                      display: "block",
                    }}
                  >
                    Description (Optional)
                  </label>
                  <textarea
                    placeholder="Describe the purpose of this channel"
                    value={newChannelDescription}
                    onChange={(e) =>
                      setNewChannelDescription(e.target.value)
                    }
                    style={{
                      ...styles.messageInput,
                      minHeight: "60px",
                      resize: "vertical",
                    }}
                  />
                </div>

                {/* Channel Type */}
                <div>
                  <label
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      marginBottom: "6px",
                      display: "block",
                    }}
                  >
                    Channel Type
                  </label>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      type="button"
                      style={{
                        ...styles.actionButton,
                        flex: 1,
                        height: "40px",
                        background:
                          newChannelType === "group"
                            ? "linear-gradient(135deg, #10b981, #059669)"
                            : theme === "dark"
                              ? "rgba(255,255,255,0.1)"
                              : "rgba(0,0,0,0.1)",
                        color:
                          newChannelType === "group"
                            ? "#fff"
                            : theme === "dark"
                              ? "#fff"
                              : "#1a1a1a",
                      }}
                      onClick={() => setNewChannelType("group")}
                    >
                      <Users size={14} style={{ marginRight: "6px" }} />
                      Group
                    </button>
                    <button
                      type="button"
                      style={{
                        ...styles.actionButton,
                        flex: 1,
                        height: "40px",
                        background:
                          newChannelType === "broadcast"
                            ? "linear-gradient(135deg, #10b981, #059669)"
                            : theme === "dark"
                              ? "rgba(255,255,255,0.1)"
                              : "rgba(0,0,0,0.1)",
                        color:
                          newChannelType === "broadcast"
                            ? "#fff"
                            : theme === "dark"
                              ? "#fff"
                              : "#1a1a1a",
                      }}
                      onClick={() => setNewChannelType("broadcast")}
                    >
                      <Radio size={14} style={{ marginRight: "6px" }} />
                      Broadcast
                    </button>
                  </div>
                </div>

                {/* Privacy Toggle */}
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                    padding: "10px",
                    borderRadius: "8px",
                    background:
                      theme === "dark"
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.05)",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isChannelPrivate}
                    onChange={(e) =>
                      setIsChannelPrivate(e.target.checked)
                    }
                    style={{ width: "16px", height: "16px" }}
                  />
                  <Shield size={14} />
                  <span style={{ fontSize: "12px", fontWeight: "600" }}>
                    Private Channel
                  </span>
                </label>

                {/* Member Selection */}
                <div>
                  <label
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      marginBottom: "6px",
                      display: "block",
                    }}
                  >
                    Add Members ({selectedMembers.length} selected)
                  </label>

                  {/* Search Input */}
                  <input
                    type="text"
                    placeholder="Search employees..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() =>
                      allEmployees.length === 0 && loadAllEmployees()
                    }
                    style={{
                      ...styles.messageInput,
                      marginBottom: "8px",
                    }}
                  />

                  {/* Selected Members Pills */}
                  {selectedMembers.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "6px",
                        padding: "8px",
                        background:
                          theme === "dark"
                            ? "rgba(255,255,255,0.05)"
                            : "rgba(0,0,0,0.05)",
                        borderRadius: "8px",
                        marginBottom: "8px",
                        maxHeight: "100px",
                        overflowY: "auto",
                      }}
                    >
                      {selectedMembers.map((memberId) => {
                        const employee = allEmployees.find(
                          (emp) => emp.infinityCode === memberId
                        );
                        return employee ? (
                          <div
                            key={memberId}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              padding: "4px 8px",
                              background:
                                "linear-gradient(135deg, #3b82f6, #2563eb)",
                              color: "#fff",
                              borderRadius: "12px",
                              fontSize: "11px",
                              fontWeight: "600",
                            }}
                          >
                            <span>{employee.sr}</span>
                            <X
                              size={12}
                              style={{ cursor: "pointer" }}
                              onClick={() =>
                                setSelectedMembers((prev) =>
                                  prev.filter((id) => id !== memberId)
                                )
                              }
                            />
                          </div>
                        ) : null;
                      })}
                    </div>
                  )}

                  {/* Employee List */}
                  <div
                    style={{
                      maxHeight: "200px",
                      overflowY: "auto",
                      border:
                        theme === "dark"
                          ? "1px solid rgba(255,255,255,0.1)"
                          : "1px solid rgba(0,0,0,0.1)",
                      borderRadius: "8px",
                      background:
                        theme === "dark"
                          ? "rgba(255,255,255,0.03)"
                          : "rgba(0,0,0,0.03)",
                    }}
                  >
                    {allEmployees
                      .filter(
                        (emp) =>
                          emp.sr
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase()) ||
                          emp.email
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase())
                      )
                      .map((employee) => (
                        <div
                          key={employee.infinityCode}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "10px 12px",
                            cursor: "pointer",
                            borderBottom:
                              theme === "dark"
                                ? "1px solid rgba(255,255,255,0.05)"
                                : "1px solid rgba(0,0,0,0.05)",
                            background: selectedMembers.includes(
                              employee.infinityCode
                            )
                              ? theme === "dark"
                                ? "rgba(59,130,246,0.15)"
                                : "rgba(59,130,246,0.1)"
                              : "transparent",
                            transition: "all 0.2s ease",
                          }}
                          onClick={() => {
                            setSelectedMembers((prev) =>
                              prev.includes(employee.infinityCode)
                                ? prev.filter(
                                  (id) => id !== employee.infinityCode
                                )
                                : [...prev, employee.infinityCode]
                            );
                          }}
                          onMouseEnter={(e) => {
                            if (
                              !selectedMembers.includes(
                                employee.infinityCode
                              )
                            ) {
                              e.currentTarget.style.background =
                                theme === "dark"
                                  ? "rgba(255,255,255,0.05)"
                                  : "rgba(0,0,0,0.05)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (
                              !selectedMembers.includes(
                                employee.infinityCode
                              )
                            ) {
                              e.currentTarget.style.background =
                                "transparent";
                            }
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "600",
                              }}
                            >
                              {employee.sr}
                            </div>
                            <div
                              style={{ fontSize: "10px", opacity: 0.7 }}
                            >
                              {employee.email}
                            </div>
                          </div>
                          {selectedMembers.includes(
                            employee.infinityCode
                          ) && <UserCheck size={14} color="#10b981" />}
                        </div>
                      ))}
                  </div>
                </div>

                {/* Create Button
                <button
                  style={{
                    ...styles.connectButton,
                    width: "100%",
                    justifyContent: "center",
                    padding: "12px",
                  }}
                  onClick={handleCreateChannel}
                >
                  <Radio size={14} />
                  Create Channel
                </button>
                */}
              </div>
            </div>
          </div>
        )}

        {/* Center Push-to-Talk Section */}
        <div style={styles.pttSection}>
          {/* Mode Selector */}
          <div style={styles.modeSelector}>
            <button
              style={
                talkMode === "hold"
                  ? { ...styles.modeButton, ...styles.activeModeButton }
                  : { ...styles.modeButton, ...styles.inactiveModeButton }
              }
              onClick={() => {
                setTalkMode("hold");
                if (isPushToTalk) stopPushToTalk();
              }}
            >
              Hold to Talk
            </button>
            <button
              style={
                talkMode === "toggle"
                  ? { ...styles.modeButton, ...styles.activeModeButton }
                  : { ...styles.modeButton, ...styles.inactiveModeButton }
              }
              onClick={() => {
                setTalkMode("toggle");
                if (isPushToTalk) stopPushToTalk();
              }}
            >
              Click to Toggle
            </button>
          </div>

          {/* Current Speaker */}
          {currentSpeaker && (
            <div style={styles.currentSpeaker}>
              <Headphones size={16} color="#10b981" />
              <span>
                🎤 Currently Speaking: <strong>{currentSpeaker}</strong>
              </span>
            </div>
          )}

          {/* Recording Timer */}
          {isRecording && (
            <div style={styles.recordingTimer}>
              <Clock size={14} />
              <span>Recording: {formatRecordingTime(recordingTime)}</span>
            </div>
          )}

          {/* Push to Talk Button */}
          <button
            ref={pushToTalkButtonRef}
            style={styles.pttButton}
            onClick={handlePTTClick}
            onMouseDown={handlePTTMouseDown}
            onMouseUp={handlePTTMouseUp}
            onMouseLeave={handlePTTMouseLeave}
            onTouchStart={handlePTTMouseDown}
            onTouchEnd={handlePTTMouseUp}
            disabled={!isConnected}
          >
            {isPushToTalk ? <Mic size={36} /> : <MicOff size={36} />}
            {isPushToTalk
              ? "TALKING NOW"
              : talkMode === "hold"
                ? "HOLD TO TALK"
                : "CLICK TO TALK"}
          </button>

          <p style={styles.pttInstructions}>
            {talkMode === "hold" ? (
              <>
                Hold the button above or press and hold{" "}
                <strong>SPACEBAR</strong> to start talking. Release to stop
                transmission.
              </>
            ) : (
              <>
                Click the button above to start talking. Click again to stop
                transmission.
              </>
            )}
            <br />
            Ensure network connection is active before use.
          </p>

          {/* Volume Control */}
          <div style={styles.volumeControl}>
            <button
              style={{
                ...styles.actionButton,
                background: isMuted
                  ? "linear-gradient(135deg, #ef4444, #dc2626)"
                  : "transparent",
                color: isMuted ? "#fff" : theme === "dark" ? "#fff" : "#1a1a1a",
                width: "32px",
                height: "32px",
              }}
              onClick={() => setIsMuted(!isMuted)}
            >
              {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>
            <input
              type="range"
              min="0"
              max="100"
              value={volume}
              onChange={(e) => setVolume(e.target.value)}
              style={styles.volumeSlider}
            />
            <span
              style={{
                fontSize: "11px",
                color:
                  theme === "dark"
                    ? "rgba(255,255,255,0.8)"
                    : "rgba(26,26,26,0.8)",
                minWidth: "28px",
                fontWeight: "700",
              }}
            >
              {volume}%
            </span>
          </div>
        </div>

        {/* Right Panel - Enhanced Users Sidebar */}
        <div style={styles.usersSidebar}>
          {/* Sidebar Header */}
          <div style={styles.sidebarHeader}>
            <div style={styles.sidebarTitle}>
              <Users size={16} />
              <span>
                Users ({stats.online}/{stats.total})
              </span>
              <div style={styles.onlineIndicator}></div>
            </div>
          </div>

          {/* Auto-manage Toggle */}
          <div
            style={styles.autoManageToggle}
            onClick={() => setAutoManageUsers(!autoManageUsers)}
          >
            <Zap size={12} />
            <span>Auto-manage</span>
            <span
              style={{
                fontSize: "10px",
                opacity: 0.8,
              }}
            >
              {autoManageUsers ? "ON" : "OFF"}
            </span>
          </div>

          {/* Users Container */}
          <div style={styles.usersContainer}>
            <div style={styles.usersList} className="users-list">
              {connectedUsers.length === 0 ? (
                <div
                  style={{
                    padding: "24px 12px",
                    textAlign: "center",
                    color:
                      theme === "dark"
                        ? "rgba(255,255,255,0.6)"
                        : "rgba(26,26,26,0.6)",
                    fontSize: "12px",
                    fontStyle: "italic",
                  }}
                >
                  No users connected
                </div>
              ) : (
                connectedUsers.map((user) => (
                  <div
                    key={user.id}
                    style={
                      selectedUser === user.id
                        ? { ...styles.userItem, ...styles.selectedUserItem }
                        : styles.userItem
                    }
                    onClick={() =>
                      setSelectedUser(selectedUser === user.id ? null : user.id)
                    }
                  >
                    <div style={styles.avatar}>
                      {user.avatar}
                      <div
                        style={{
                          ...styles.statusBadge,
                          background: getUserStatusColor(user.status),
                        }}
                      ></div>
                    </div>

                    <div style={styles.userInfo}>
                      <div style={styles.userName}>{user.name}</div>
                      <div style={styles.userStatus}>
                        <span
                          style={{
                            color: getUserStatusColor(user.status),
                            fontSize: "10px",
                          }}
                        >
                          ●
                        </span>
                        {user.status === "online"
                          ? "Online"
                          : user.status === "talking"
                            ? "Talking"
                            : "Offline"}
                      </div>
                      <div style={styles.userMeta}>
                        Joined {formatDuration(user.joinTime)}
                      </div>
                    </div>

                    <div style={styles.permissionControls}>
                      <div style={styles.permissionButtons}>
                        <button
                          title="Allow"
                          style={getPermissionButtonStyle(
                            user.permission,
                            "allowed"
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            updateUserPermission(user.id, "allowed");
                          }}
                        >
                          <UserCheck size={12} />
                        </button>

                        <button
                          title="Pending"
                          style={getPermissionButtonStyle(
                            user.permission,
                            "pending"
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            updateUserPermission(user.id, "pending");
                          }}
                        >
                          <Settings size={12} />
                        </button>

                        <button
                          title="Block"
                          style={getPermissionButtonStyle(
                            user.permission,
                            "blocked"
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            updateUserPermission(user.id, "blocked");
                          }}
                        >
                          <UserX size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Statistics Panel */}
          <div style={styles.statsPanel}>
            <div style={styles.statsTitle}>
              <Activity size={12} />
              Network Statistics
            </div>

            <div style={styles.statsGrid}>
              <div style={styles.statItem}>
                <span style={styles.statLabel}>Online:</span>
                <span style={{ ...styles.statValue, color: "#10b981" }}>
                  {stats.online}
                </span>
              </div>

              <div style={styles.statItem}>
                <span style={styles.statLabel}>Total:</span>
                <span
                  style={{
                    ...styles.statValue,
                    color: theme === "dark" ? "#fff" : "#1a1a1a",
                  }}
                >
                  {stats.total}
                </span>
              </div>

              <div style={styles.statItem}>
                <span style={styles.statLabel}>Allowed:</span>
                <span style={{ ...styles.statValue, color: "#10b981" }}>
                  {stats.allowed}
                </span>
              </div>

              <div style={styles.statItem}>
                <span style={styles.statLabel}>Pending:</span>
                <span style={{ ...styles.statValue, color: "#f59e0b" }}>
                  {stats.pending}
                </span>
              </div>

              <div style={styles.statItem}>
                <span style={styles.statLabel}>Blocked:</span>
                <span style={{ ...styles.statValue, color: "#ef4444" }}>
                  {stats.blocked}
                </span>
              </div>

              <div style={styles.statItem}>
                <span style={styles.statLabel}>Latency:</span>
                <span
                  style={{
                    ...styles.statValue,
                    color:
                      networkLatency < 50
                        ? "#10b981"
                        : networkLatency < 100
                          ? "#f59e0b"
                          : "#ef4444",
                  }}
                >
                  {networkLatency}ms
                </span>
              </div>
              {/* Online Users Modal for Direct Chat */}
              {showOnlineUsersModal && (
                <div
                  style={styles.modal}
                  onClick={(e) =>
                    e.target === e.currentTarget &&
                    setShowOnlineUsersModal(false)
                  }
                >
                  <div style={styles.modalContent}>
                    <div style={styles.modalHeader}>
                      <h3 style={styles.modalTitle}>
                        <Users size={18} />
                        Start Direct Chat
                      </h3>
                      <button
                        style={styles.closeButton}
                        onClick={() => setShowOnlineUsersModal(false)}
                      >
                        <X size={16} />
                      </button>
                    </div>

                    <div style={styles.messagesContainer}>
                      {allOnlineUsers.length === 0 ? (
                        <div
                          style={{
                            textAlign: "center",
                            padding: "24px",
                            color:
                              theme === "dark"
                                ? "rgba(255,255,255,0.6)"
                                : "rgba(26,26,26,0.6)",
                          }}
                        >
                          No users online
                        </div>
                      ) : (
                        allOnlineUsers.map((user) => (
                          <div
                            key={user.infinityCode}
                            style={styles.userItem}
                            onClick={() =>
                              handleStartDirectChat(user.infinityCode)
                            }
                          >
                            <div style={styles.avatar}>
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                            <div style={styles.userInfo}>
                              <div style={styles.userName}>{user.name}</div>
                              <div style={styles.userStatus}>
                                Click to start chat
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Messages Modal */}
      {showMessagesModal && (
        <div
          style={styles.modal}
          onClick={(e) =>
            e.target === e.currentTarget && setShowMessagesModal(false)
          }
        >
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>
                <MessageCircle size={18} />
                Group Messages
              </h3>
              <button
                style={styles.closeButton}
                onClick={() => setShowMessagesModal(false)}
              >
                <X size={16} />
              </button>
            </div>

            {/* Messages Container */}
            <div
              style={styles.messagesContainer}
              className="messages-container"
            >
              {messages.map((message) => (
                <div
                  key={message.id}
                  style={
                    message.type === "system"
                      ? { ...styles.message, ...styles.systemMessage }
                      : styles.message
                  }
                >
                  {message.type !== "system" && (
                    <div style={styles.messageHeader}>
                      <span style={{ fontWeight: "700" }}>
                        {message.sender}
                      </span>
                      <span>{formatTime(message.timestamp)}</span>
                    </div>
                  )}

                  {message.type === "text" && (
                    <div style={styles.messageContent}>{message.content}</div>
                  )}

                  {message.type === "voice" && (
                    <div style={styles.voiceMessage}>
                      <button
                        style={
                          playingAudio === message.id
                            ? styles.playButtonActive
                            : styles.playButton
                        }
                        onClick={() =>
                          message.audioUrl &&
                          playAudio(message.audioUrl, message.id)
                        }
                      >
                        {playingAudio === message.id ? (
                          <VolumeX size={12} />
                        ) : (
                          <Play size={12} />
                        )}
                      </button>
                      <span style={styles.messageContent}>
                        Voice Message - {message.duration}
                      </span>
                      {message.audioUrl && (
                        <a
                          href={message.audioUrl}
                          download={`voice-message-${message.sender}-${message.id}.wav`}
                          style={{
                            color: "#3b82f6",
                            textDecoration: "none",
                            padding: "4px",
                            borderRadius: "4px",
                            transition: "all 0.3s ease",
                          }}
                        >
                          <Download size={12} />
                        </a>
                      )}
                    </div>
                  )}
                  {message.type === "image" && (
                    <div>
                      <div style={styles.messageContent}>{message.content}</div>
                      <img
                        src={message.imageUrl}
                        alt="Shared"
                        style={styles.messageImage}
                        onClick={() => window.open(message.imageUrl, "_blank")}
                      />
                    </div>
                  )}

                  {message.type === "system" && (
                    <div style={styles.messageContent}>{message.content}</div>
                  )}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <form onSubmit={sendMessage} style={styles.inputContainer}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: "none" }}
              />

              <button
                type="button"
                style={{
                  ...styles.actionButton,
                  background: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
                  color: "#fff",
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <Image size={16} />
              </button>

              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message here..."
                style={styles.messageInput}
              />

              <button
                type="submit"
                style={{
                  ...styles.actionButton,
                  background: "linear-gradient(135deg, #10b981, #059669)",
                  color: "#fff",
                }}
              >
                <Send size={16} />
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Enhanced CSS Animations and Scrollbar Styles */}
      <style jsx>{`
        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.7;
            transform: scale(1.02);
          }
        }

        .messages-container::-webkit-scrollbar,
        .users-list::-webkit-scrollbar {
          width: 6px;
        }

        .messages-container::-webkit-scrollbar-track,
        .users-list::-webkit-scrollbar-track {
          background: ${theme === "dark"
          ? "rgba(255,255,255,0.05)"
          : "rgba(0,0,0,0.05)"};
          border-radius: 3px;
        }

        .messages-container::-webkit-scrollbar-thumb,
        .users-list::-webkit-scrollbar-thumb {
          background: ${theme === "dark"
          ? "rgba(255,255,255,0.2)"
          : "rgba(0,0,0,0.2)"};
          border-radius: 3px;
          transition: all 0.3s ease;
        }

        .messages-container::-webkit-scrollbar-thumb:hover,
        .users-list::-webkit-scrollbar-thumb:hover {
          background: ${theme === "dark"
          ? "rgba(255,255,255,0.3)"
          : "rgba(0,0,0,0.3)"};
        }

        input[type="range"]::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: linear-gradient(135deg, #10b981, #059669);
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(16, 185, 129, 0.4);
          transition: all 0.3s ease;
        }

        input[type="range"]::-webkit-slider-thumb:hover {
          transform: scale(1.1);
          box-shadow: 0 3px 8px rgba(16, 185, 129, 0.5);
        }

        input[type="range"]::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: linear-gradient(135deg, #10b981, #059669);
          cursor: pointer;
          border: none;
          box-shadow: 0 2px 6px rgba(16, 185, 129, 0.4);
          transition: all 0.3s ease;
        }

        button:hover {
          transform: translateY(-1px);
        }

        button:disabled {
          cursor: not-allowed !important;
          opacity: 0.6;
          transform: none !important;
        }

        * {
          user-select: none;
          box-sizing: border-box;
        }

        input,
        textarea {
          user-select: text;
        }

        html,
        body,
        #root {
          margin: 0;
          padding: 0;
          overflow: hidden;
          width: 100%;
          height: 100%;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes modalSlideIn {
          from {
            transform: scale(0.9) translateY(20px);
            opacity: 0;
          }
          to {
            transform: scale(1) translateY(0);
            opacity: 1;
          }
        }

        @keyframes slideIn {
          from {
            transform: translateX(-10px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        button:hover:not(:disabled) {
          transform: translateY(-2px);
          filter: brightness(1.1);
        }

        button:active:not(:disabled) {
          transform: translateY(0);
        }

        .playButton:hover {
          transform: scale(1.15);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        }
      `}</style>
    </div>
  );
};
export default Connector;